import { useEffect } from 'react';
import { useAppSelector } from '@/store/hooks';
import {
    selectPrimaryColor,
    selectSurfaceTone,
    selectTextTone,
    selectTheme,
} from '@/store/slices/uiSlice';
import { COP32_DARK_SURFACE } from '@/theme/cop32DarkSurface';

const PRIMARY_PALETTES = {
    green: {
        '50': '#ecfdf5',
        '100': '#d1fae5',
        '200': '#a7f3d0',
        '300': '#6ee7b7',
        '400': '#34d399',
        '500': '#10b981',
        '600': '#059669',
        '700': '#047857',
        '800': '#065f46',
        '900': '#064e3b',
        '950': '#022c22',
    },
    yellow: {
        '50': '#fefce8',
        '100': '#fef9c3',
        '200': '#fef08a',
        '300': '#fde047',
        '400': '#facc15',
        '500': '#eab308',
        '600': '#ca8a04',
        '700': '#a16207',
        '800': '#854d0e',
        '900': '#713f12',
        '950': '#422006',
    },
    blue: {
        '50': '#eff6ff',
        '100': '#dbeafe',
        '200': '#bfdbfe',
        '300': '#93c5fd',
        '400': '#60a5fa',
        '500': '#3b82f6',
        '600': '#2563eb',
        '700': '#1d4ed8',
        '800': '#1e40af',
        '900': '#1e3a8a',
        '950': '#172554',
    },
    cyan: {
        '50': '#ecfeff',
        '100': '#cffafe',
        '200': '#a5f3fc',
        '300': '#67e8f9',
        '400': '#22d3ee',
        '500': '#06b6d4',
        '600': '#0891b2',
        '700': '#0e7490',
        '800': '#155e75',
        '900': '#164e63',
        '950': '#083344',
    },
    purple: {
        '50': '#faf5ff',
        '100': '#f3e8ff',
        '200': '#e9d5ff',
        '300': '#d8b4fe',
        '400': '#c084fc',
        '500': '#a855f7',
        '600': '#7c3aed',
        '700': '#6d28d9',
        '800': '#5b21b6',
        '900': '#4c1d95',
        '950': '#2e1065',
    },
    orange: {
        '50': '#fff7ed',
        '100': '#ffedd5',
        '200': '#fed7aa',
        '300': '#fdba74',
        '400': '#fb923c',
        '500': '#f97316',
        '600': '#ea580c',
        '700': '#c2410c',
        '800': '#9a3412',
        '900': '#7c2d12',
        '950': '#431407',
    },
    pink: {
        '50': '#fdf2f8',
        '100': '#fce7f3',
        '200': '#fbcfe8',
        '300': '#f9a8d4',
        '400': '#f472b6',
        '500': '#ec4899',
        '600': '#db2777',
        '700': '#be185d',
        '800': '#9d174d',
        '900': '#831843',
        '950': '#500724',
    },
    red: {
        '50': '#fef2f2',
        '100': '#fee2e2',
        '200': '#fecaca',
        '300': '#fca5a5',
        '400': '#f87171',
        '500': '#ef4444',
        '600': '#dc2626',
        '700': '#b91c1c',
        '800': '#991b1b',
        '900': '#7f1d1d',
        '950': '#450a0a',
    },
    white: {
        '50': '#ffffff',
        '100': '#ffffff',
        '200': '#ffffff',
        '300': '#ffffff',
        '400': '#ffffff',
        '500': '#f8fafc',
        '600': '#e2e8f0',
        '700': '#cbd5e1',
        '800': '#94a3b8',
        '900': '#64748b',
        '950': '#334155',
    },
};

function applyPrimaryCssVars(primaryColor) {
    if (typeof document === 'undefined') return;
    const palette = PRIMARY_PALETTES[primaryColor] ?? PRIMARY_PALETTES.green;
    const root = document.documentElement;

    for (const shade of [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]) {
        root.style.setProperty(`--primary-${shade}`, palette[String(shade)]);
    }
    root.style.setProperty('--primary-default', palette['600']);
    const primaryFg = primaryColor === 'white' ? '#0f172a' : '#ffffff';
    root.style.setProperty('--primary-foreground', primaryFg);
    root.style.setProperty('--ring', palette['600']);
    root.style.setProperty('--accent', palette['50']);
    root.style.setProperty('--accent-foreground', primaryFg);
}

const SURFACES = {
    surfaceLight0: {
        background: '#f8fafc',
        card: '#ffffff',
        popover: '#ffffff',
        foreground: '#0f172a',
        muted: '#f1f5f9',
        mutedForeground: '#64748b',
        border: '#e2e8f0',
        input: '#e2e8f0',
        ring: '#2563eb',
        isDark: false,
    },
    surfaceLight1: {
        background: '#f8f9fa',
        card: '#ffffff',
        popover: '#ffffff',
        foreground: '#1a1a2e',
        muted: '#f1f3f5',
        mutedForeground: '#5b6d77',
        border: '#dee2e6',
        input: '#dee2e6',
        ring: '#0b3d2e',
        isDark: false,
    },
    surfaceLight2: {
        background: '#eef2f7',
        card: '#f8fafc',
        popover: '#f8fafc',
        foreground: '#111827',
        muted: '#e5e7eb',
        mutedForeground: '#6b7280',
        border: '#d1d5db',
        input: '#d1d5db',
        ring: '#0b3d2e',
        isDark: false,
    },
    surfaceLight3: {
        background: '#e5e7eb',
        card: '#f3f4f6',
        popover: '#f3f4f6',
        foreground: '#111827',
        muted: '#d1d5db',
        mutedForeground: '#4b5563',
        border: '#cbd5e1',
        input: '#cbd5e1',
        ring: '#0b3d2e',
        isDark: false,
    },
    surfaceLight4: {
        background: '#d1d5db',
        card: '#e5e7eb',
        popover: '#e5e7eb',
        foreground: '#0f172a',
        muted: '#cbd5e1',
        mutedForeground: '#475569',
        border: '#94a3b8',
        input: '#94a3b8',
        ring: '#0b3d2e',
        isDark: false,
    },
    surfaceMid0: {
        background: '#8b949e',
        card: '#adb5bd',
        popover: '#adb5bd',
        foreground: '#0f172a',
        muted: '#6b7280',
        mutedForeground: '#111827',
        border: '#475569',
        input: '#475569',
        ring: '#2563eb',
        isDark: true,
    },
    surfaceMid1: {
        background: '#374151',
        card: '#1f2937',
        popover: '#1f2937',
        foreground: '#e5e7eb',
        muted: '#374151',
        mutedForeground: '#9ca3af',
        border: '#4b5563',
        input: '#4b5563',
        ring: '#2563eb',
        isDark: true,
    },
    surfaceDark0: {
        background: '#0f1419',
        card: '#1a2332',
        popover: '#1a2332',
        foreground: '#e8eaed',
        muted: '#1a2332',
        mutedForeground: '#8b949e',
        border: '#2d3640',
        input: '#2d3640',
        ring: '#3d9e74',
        isDark: true,
    },
    surfaceDark1: {
        background: '#0b1220',
        card: '#111827',
        popover: '#111827',
        foreground: '#e5e7eb',
        muted: '#0f172a',
        mutedForeground: '#9ca3af',
        border: '#1f2937',
        input: '#1f2937',
        ring: '#3b82f6',
        isDark: true,
    },
    surfaceDark2: {
        background: '#0a0f14',
        card: '#0f172a',
        popover: '#0f172a',
        foreground: '#e5e7eb',
        muted: '#0b1220',
        mutedForeground: '#93c5fd',
        border: '#111827',
        input: '#111827',
        ring: '#3b82f6',
        isDark: true,
    },
};

/**
 * Applies CSS variables from Redux: primary palette, surface/text (light mode), or COP32 dark preset when theme is dark.
 * Keeps <html class="dark"> in sync with surface `isDark` or dark mode toggle.
 */
export function ThemeProvider({ children }) {
    const primaryColor = useAppSelector(selectPrimaryColor);
    const surfaceTone = useAppSelector(selectSurfaceTone);
    const textTone = useAppSelector(selectTextTone);
    const theme = useAppSelector(selectTheme);
    const isDarkMode = theme === 'dark';

    useEffect(() => {
        applyPrimaryCssVars(primaryColor);
    }, [primaryColor]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const s = isDarkMode ? COP32_DARK_SURFACE : (SURFACES[surfaceTone] ?? SURFACES.surfaceLight1);
        document.documentElement.classList.toggle('dark', Boolean(s.isDark));

        const root = document.documentElement;
        root.style.setProperty('--background', s.background);
        root.style.setProperty('--foreground', s.foreground);
        root.style.setProperty('--card', s.card);
        root.style.setProperty('--card-foreground', s.foreground);
        root.style.setProperty('--popover', s.popover);
        root.style.setProperty('--popover-foreground', s.foreground);
        root.style.setProperty('--muted', s.muted);
        root.style.setProperty('--muted-foreground', s.mutedForeground);
        root.style.setProperty('--border', s.border);
        root.style.setProperty('--input', s.input);
        root.style.setProperty('--ring', s.ring);
    }, [surfaceTone, isDarkMode]);

    useEffect(() => {
        if (typeof document === 'undefined') return;

        if (isDarkMode) {
            const root = document.documentElement;
            root.style.setProperty('--foreground', COP32_DARK_SURFACE.foreground);
            root.style.setProperty('--card-foreground', COP32_DARK_SURFACE.foreground);
            root.style.setProperty('--popover-foreground', COP32_DARK_SURFACE.foreground);
            root.style.setProperty('--muted-foreground', COP32_DARK_SURFACE.mutedForeground);
            return;
        }

        // Text selection drives foreground shades; it should not change surface colors.
        const map = {
            textDark0: { foreground: '#1a1a2e', mutedForeground: '#5b6d77' },
            textDark1: { foreground: '#0f172a', mutedForeground: '#64748b' },
            textLight0: { foreground: '#e8eaed', mutedForeground: '#8b949e' },
            textLight1: { foreground: '#e5e7eb', mutedForeground: '#9ca3af' },
            textMuted0: { foreground: '#475569', mutedForeground: '#64748b' },
            textMuted1: { foreground: '#93c5fd', mutedForeground: '#9ca3af' },
        };
        const t = map[textTone] ?? map.textDark0;

        const root = document.documentElement;
        root.style.setProperty('--foreground', t.foreground);
        root.style.setProperty('--card-foreground', t.foreground);
        root.style.setProperty('--popover-foreground', t.foreground);
        root.style.setProperty('--muted-foreground', t.mutedForeground);
    }, [textTone, isDarkMode]);

    return children;
}

export default ThemeProvider;
